import Helper from './helper';
/**
 * Creates a CSV file from an array of objects
 * @param {Array} data - Array of objects to convert to CSV
 * @param {Array} headers - Array of header strings for CSV columns
 * @param {Array} fields - Array of field names to extract from each object
 * @param {string} fileName - Name of the CSV file (without extension)
 * @param {Function} onFileCreated - Optional callback function when file is created
 * @returns {Promise<string>} Promise that resolves to base64 encoded CSV content
 */
export const createCsv = (data, headers =[], fields =[], fileName = 'export' ,stringifyArrayObjects = true) => {
    // Create CSV content
    const csvContent = data.map(item => {
      return fields.map(field => {
        const value = getNestedValue(item, field);
        // Handle arrays and objects by converting them to strings
        if (Array.isArray(value)) {
          if(stringifyArrayObjects){
            return value.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join('; ');
          }else{
            return value.map(v => {
              if (typeof v === 'object' && v !== null) {
                return Object.values(v).filter(n => n).join('-');
              }
              return String(v);
            }).join('; ');
          }
        }
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value);
        }
        // Escape commas and quotes in string values
        if (typeof value === 'boolean') {
          return value ? 'true' : 'false';
        }
        const stringValue = String(value || '');
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',');
    }).join('\n');
    
    // Add CSV header
    var header = headers && headers.length > 0 ? headers.join(',') : '';
    const fileContent = `${header}\n${csvContent}`;
    
    const csvWithBOM = '\uFEFF' + fileContent;
    // Create Blob from CSV content
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; 
        
        const fileObject = {
          fileContentBase64: base64,
          fileName: fileName,
          fileSizeInMb: blob.size / 1000000,
          fileType: 'csv',
          id: Helper.genarateUuid4(),
        };
      
        
        resolve(fileObject);
      };
      reader.readAsDataURL(blob);
    });
  };
  
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  };
  

  export const downloadFile = (base64Content, fileName, fileExtension) => {
    let mimeType;

  switch (fileExtension.toLowerCase()) {
    case 'png':
      mimeType = 'image/png';
      break;
    case 'jpg':
    case 'jpeg':
      mimeType = 'image/jpeg';
      break;
    case 'pdf':
      mimeType = 'application/pdf';
      break;
    case 'txt':
      mimeType = 'text/plain';
      break;
    case 'csv':
      mimeType = 'text/csv';
      break;
    case 'doc':
      mimeType = 'application/msword';
      break;
    case 'docx':
      mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      break;
    case 'xls':
      mimeType = 'application/vnd.ms-excel';
      break;
    case 'xlsx':
      mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      break;
    default:
      mimeType = 'application/octet-stream'; // fallback
  }
    const dataUrl = `data:${mimeType};base64,${base64Content}`;
  
    // Create an anchor element and set its href to the data URL.
    const downloadLink = document.createElement('a');
    downloadLink.href = dataUrl;
    downloadLink.download = fileName;
  
    // Append the link to the DOM (not visible) and trigger a click to start the download.
    document.body.appendChild(downloadLink);
    downloadLink.click();
  
    // Remove the link from the document once the download is triggered.
    document.body.removeChild(downloadLink);
  }

  export const downloadCsv = (data, headers, fields, fileName = 'export') => {
    const csvContent = data.map(item => {
      return fields.map(field => {
        const value = getNestedValue(item, field);
        if (Array.isArray(value)) {
          return value.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join('; ');
        }
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value);
        }
        const stringValue = String(value || '');
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',');
    }).join('\n');
    
    const csvWithHeader = `${headers.join(',')}\n${csvContent}`;
    const blob = new Blob([csvWithHeader], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  export const getFileExtension =(fileType) => {
    var fileExtension = '';
    switch (fileType) {
      case 'jpg':
      case 'jpeg':
        fileExtension = 'jpg';
        break;
      case 'xls':
      case 'vnd.ms-excel':
        fileExtension = 'xls';
        break;   
     default:
        fileExtension = fileType;
        break;
    }
    return fileExtension;
  }